import moment from 'moment-timezone';
import BottomSheet from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheet/BottomSheet';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { ActivityIndicator, FlatList, SafeAreaView, useDripsyTheme, View } from 'dripsy';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { RootStackRoutes } from 'types';
import { UniIconButton } from '@/design-system/components/buttons';
import { UniInput } from '@/design-system/components/inputs';
import { TextTitle } from '@/design-system/components/typography';
import { keyGenerator } from '@/utils/keyGenerator';
import { ActivityHistoryCardNative } from '../../components/cards/ActivityHistoryCardNative';
import { ActivityHistoryFilterParams } from '../../components/activityHistory/ActivityHistoryFilter';
import { EarnStackParamList, EarnStackRoutes } from '../../earn.navigator';
import { ActivityHistoryProps } from './ActivityHistory.screen';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { getEarnCategoryTranslationKey } from '../../utils/getEarnCategoryTranslationKey';
import { EarnContext } from '../../contexts/useEarnContext';

export const ActivityHistoryNative: React.FC<ActivityHistoryProps> = ({
  loading,
  filter,
  subscriptions,
  paginationParams,
  onFilterChange
}) => {
  const { t } = useTranslation('earn');
  const { theme } = useDripsyTheme();
  const bottomSheetRef = useRef<BottomSheet>(null);
  const navigation = useNavigation();
  const earnNavigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();
  const earnContext = useContext(EarnContext);

  const [filterValues, setFilterValues] = useState<ActivityHistoryFilterParams>(filter);

  useEffect(() => {
    onFilterChange(filterValues);
  }, [filterValues]);

  return (
    <SafeAreaView sx={{ height: '100%', backgroundColor: '$backgroundSecondary' }}>
      <View sx={{ flex: 1 }}>
        {/* Overview */}
        <View
          sx={{
            flex: 1,
            borderTopLeftRadius: 15,
            borderTopRightRadius: 15,
            backgroundColor: '$backgroundPrimary',
            paddingY: '$5'
          }}>
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', paddingX: '$4' }}>
            <TextTitle bold>{t('earn-invest::glossary.activity-history')}</TextTitle>
            <View sx={{ flexDirection: 'row', justifyContent: 'flex-end', gap: '$3' }}>
              <UniIconButton
                icon="activityHistoryFilter"
                onPress={() => {
                  earnNavigation.navigate(EarnStackRoutes.BottomSheetFilter, {
                    title: t('earn-invest::glossary.mobile-filter'),
                    bottomSheetRef,
                    options: [
                      {
                        label: t('earn-invest::glossary.type'),
                        name: 'action',
                        singleSelect: true,
                        options: [
                          { label: t('earn-invest::glossary.subscribe'), value: 'SUBSCRIPTION' },
                          { label: t('earn-invest::glossary.redeem'), value: 'REDEMPTION' }
                        ]
                      },
                      {
                        label: t('earn-invest::glossary.category'),
                        name: 'category',
                        singleSelect: true,
                        options: earnContext.offerCategories.map((category) => ({
                          label: t(getEarnCategoryTranslationKey(category)),
                          value: category
                        }))
                      }
                    ],
                    values: { action: filterValues.action, category: filterValues.category },
                    filteringNumber: 1,
                    onClose: () => earnNavigation.goBack(),
                    onChange: (tempFilters) => {
                      let newFilterValues = { ...filterValues };
                      newFilterValues = {
                        ...newFilterValues,
                        action: tempFilters.action,
                        category: tempFilters.category
                      };
                      setFilterValues(newFilterValues);
                    }
                  });
                }}
              />
              <UniIconButton
                icon="activityHistoryDatePicker"
                onPress={() => {
                  earnNavigation.navigate(EarnStackRoutes.BottomSheetDatePicker, {
                    value: {
                      from: moment(filterValues.fromDate),
                      to: moment(filterValues.toDate)
                    },
                    onChange: (value) => {
                      setFilterValues({
                        ...filterValues,
                        fromDate: value.from?.toDate(),
                        toDate: value.to?.toDate()
                      });
                    }
                  });
                }}
              />
            </View>
          </View>
          <View sx={{ height: '$5', paddingX: '$4' }} />
          <View sx={{ paddingX: '$4' }}>
            <UniInput
              prefixIcon="search"
              onValueChange={(value) => setFilterValues((prev) => ({ ...prev, search: value && value.toUpperCase() }))}
              placeholder={t('earn-invest::glossary.search-by-asset')}
              sx={{ width: '100%', marginX: '$4', marginY: '$5' }}
            />
          </View>
          <View sx={{ flex: 1 }}>
            <FlatList
              sx={{ flex: 1, paddingX: '$4' }}
              scrollEnabled={true}
              showsVerticalScrollIndicator={false}
              data={subscriptions}
              keyExtractor={(_, index) => keyGenerator(`subscription-card-${index}`)}
              renderItem={({ item }) => (
                <ActivityHistoryCardNative
                  subscription={item}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.Earn, {
                      screen: EarnStackRoutes.ActivityHistoryDetails,
                      params: { subscription: item }
                    });
                  }}
                />
              )}
              onEndReached={() => {
                paginationParams.onNextPage();
              }}
              onEndReachedThreshold={0.2}
            />
            {loading && (
              <View sx={{ padding: '$5' }}>
                <ActivityIndicator size="large" color={theme.colors?.$disable || '#F0F0F0'} />
              </View>
            )}
          </View>
        </View>
      </View>
      {/* </ScrollView> */}
    </SafeAreaView>
  );
};
